import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaSpinner, FaExclamationTriangle, FaKey, FaCheckCircle, FaDesktop, FaDownload, FaFileAlt } from 'react-icons/fa';
import useCreditMode from '../../hooks/useCreditMode';

const isLicenseValid = (lic) =>
  lic.status === 'active' &&
  (!lic.expires_at || new Date(lic.expires_at) > new Date()) &&
  Boolean(lic.machine_code);

const isUnallocated = (lic) => !lic.machine_code;

const LicenseManager = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [softwareDownloading, setSoftwareDownloading] = useState(false);
  const [softwareDownloads, setSoftwareDownloads] = useState([]);
  const [showDownloads, setShowDownloads] = useState(false);
  const [error, setError] = useState('');
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  // Credit mode sells credits, not licenses: this surface becomes pure Downloads.
  const creditMode = useCreditMode();

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/license/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenses(data.licenses || []);
      } else {
        setError('Failed to fetch licenses');
      }
    } catch (err) {
      console.error('Error fetching licenses:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const stats = useMemo(() => ({
    allotted: licenses.length,
    valid: licenses.filter(isLicenseValid).length,
    unallocated: licenses.filter(isUnallocated).length,
  }), [licenses]);

  const hasActiveLicense = licenses.some((lic) => lic.status === 'active');

  const downloadSoftware = async () => {
    try {
      setSoftwareDownloading(true);
      setError('');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/software/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.downloads?.length > 0) {
          setSoftwareDownloads(data.downloads);
          setShowDownloads(true);
          if (data.downloads.length === 1) {
            window.open(data.downloads[0].url, '_blank');
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get download link');
      }
    } catch (err) {
      console.error('Error downloading software:', err);
      setError('Failed to download software');
    } finally {
      setSoftwareDownloading(false);
    }
  };

  const statCards = [
    {
      key: 'allotted',
      label: 'Licenses Allotted',
      value: stats.allotted,
      icon: FaKey,
      accent: isDarkMode ? 'text-brand-purple' : 'text-brand-purple',
      bg: isDarkMode ? 'bg-brand-purple/10' : 'bg-brand-purple/5',
    },
    {
      key: 'valid',
      label: 'Valid',
      value: stats.valid,
      icon: FaCheckCircle,
      accent: isDarkMode ? 'text-green-400' : 'text-green-600',
      bg: isDarkMode ? 'bg-green-500/10' : 'bg-green-50',
    },
    {
      key: 'unallocated',
      label: 'Unallocated',
      value: stats.unallocated,
      icon: FaDesktop,
      accent: isDarkMode ? 'text-blue-400' : 'text-blue-600',
      bg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <FaSpinner className="animate-spin w-8 h-8 text-brand-purple" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <FaExclamationTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {error}
        </p>
        <button
          onClick={fetchLicenses}
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-purple to-brand-red hover:from-brand-purple-dark hover:to-brand-red-dark rounded-lg transition-all duration-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {!creditMode && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map(({ key, label, value, icon: Icon, accent, bg }) => (
          <div
            key={key}
            className={`p-5 rounded-xl border transition-all duration-300 ${
              isDarkMode
                ? 'bg-gray-800/30 border-gray-700/30'
                : 'bg-white/50 border-gray-200/50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${bg}`}>
                <Icon className={`w-5 h-5 ${accent}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {label}
                </p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {!creditMode && licenses.length === 0 && (
        <p className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Purchase a license to get started with ZapRF.
        </p>
      )}

      {/* Downloading is an account entitlement in credit mode -- no license,
          activation or machine code required. */}
      {(creditMode || hasActiveLicense) && (
        <div className={`p-5 rounded-xl border transition-all duration-300 ${
          isDarkMode
            ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700/30'
            : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200/50'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-600/30' : 'bg-blue-100'}`}>
                <FaDownload className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ZapRF Software
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Download ZapRF installer
                </p>
              </div>
            </div>
            <button
              onClick={downloadSoftware}
              disabled={softwareDownloading}
              className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
            >
              {softwareDownloading ? (
                <>
                  <FaSpinner className="animate-spin w-4 h-4" />
                  <span>Preparing...</span>
                </>
              ) : (
                <>
                  <FaDownload className="w-4 h-4" />
                  <span>Download Setup</span>
                </>
              )}
            </button>
          </div>

          {showDownloads && softwareDownloads.length > 0 && (
            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-blue-700/30' : 'border-blue-200/50'}`}>
              <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Available Versions ({softwareDownloads.length})
              </h4>
              <div className="space-y-2">
                {softwareDownloads.map((download, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-800/50' : 'bg-white/70'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FaFileAlt className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {download.filename}
                      </span>
                    </div>
                    <button
                      onClick={() => window.open(download.url, '_blank')}
                      className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all duration-200"
                    >
                      <FaDownload className="w-3 h-3" />
                      <span>Download</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!creditMode && (
        <button
          onClick={fetchLicenses}
          className="w-full py-2 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-purple to-brand-red hover:from-brand-purple-dark hover:to-brand-red-dark rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
        >
          Refresh
        </button>
      )}
    </div>
  );
};

export default LicenseManager;
